import { ROLES } from 'app/auth/authRoles';
import { INTERNAL_COMPANY_HIDDEN_PAGES } from 'app/auth/authUtils';
import _ from 'lodash';

import { BARE_CLAIM_PAGE_CODES, ClaimValue, PageCodeComparisonMethod } from 'constants/claims';

export const checkIsAdmin = masterRole =>
  masterRole?.some(role => [ROLES.ADMIN, ROLES.ALL_CLAIMS_CYPRESS_USER].includes(role));

export const getPagePermissions = pageName => permissions => {
  if (!pageName) {
    return {
      isManageAllowed: true,
      isEditAllowed: true,
      isStrictEditAllowed: true,
      isViewAllowed: true,
    };
  }

  if (_.isObject(pageName)) {
    const { comparisonType, pageCodes } = pageName;

    const comparisonMethod = PageCodeComparisonMethod[comparisonType];

    const isManage = pageCodes[comparisonMethod](
      name =>
        !!permissions?.includes(`${name}.${ClaimValue.MANAGE}`) ||
        !!permissions?.includes(`${name}.${ClaimValue.ACCESS}`),
    );

    const isEdit = pageCodes[comparisonMethod](
      name => !!permissions?.includes(`${name}.${ClaimValue.EDIT}`),
    );
    const isView = pageCodes[comparisonMethod](
      name => !!permissions?.includes(`${name}.${ClaimValue.VIEW}`),
    );

    return {
      isManageAllowed: isManage,
      isEditAllowed: isManage || isEdit,
      isStrictEditAllowed: isEdit,
      isViewAllowed: isManage || isEdit || isView,
    };
  }

  // A bare-claim page code's value IS the granted claim (no verb suffix); the holder of that
  // single atomic claim has full authoring rights, so treat exact membership as manage.
  if (BARE_CLAIM_PAGE_CODES.has(pageName)) {
    const isBareClaimManage = !!permissions?.includes(pageName);

    return {
      isManageAllowed: isBareClaimManage,
      isEditAllowed: isBareClaimManage,
      isStrictEditAllowed: isBareClaimManage,
      isViewAllowed: isBareClaimManage,
    };
  }

  // `.access` deliberately satisfies isManageAllowed: for several page codes `.manage` is granted to
  // NO role and `.access` is the claim actually issued (e.g. formBundleSignatureRequest), so
  // "tightening" this to a literal `.manage` check would dead-button those features for every user.
  // This is a client-side affordance gate only — the server re-resolves claims per request.
  const isManage =
    !!permissions?.includes(`${pageName}.${ClaimValue.MANAGE}`) ||
    !!permissions?.includes(`${pageName}.${ClaimValue.ACCESS}`);
  const isEdit = !!permissions?.includes(`${pageName}.${ClaimValue.EDIT}`);
  const isView = !!permissions?.includes(`${pageName}.${ClaimValue.VIEW}`);

  return {
    isManageAllowed: isManage,
    isEditAllowed: isManage || isEdit,
    // The literal `.edit` grant, with no `.manage`/`.access` alias — for surfaces whose server
    // policy accepts ONLY `<page>.edit` (e.g. LoanEditorPolicy.Edit = { loanEditor.edit }), where
    // the alias above would offer a control the API then rejects.
    isStrictEditAllowed: isEdit,
    isViewAllowed: isManage || isEdit || isView,
  };
};

export const hasPermission = ({
  pageCode,
  permissions,
  masterRole,
  isGuestOnly,
  isAllowedWithoutRoles,
  isFromInternalCompany,
  skipPermissionCheck,
  isAuthAgnostic,
}) => {
  // Page is accessible regardless of auth state (e.g. token handoff)
  if (isAuthAgnostic) {
    return true;
  }

  // Logged in and no role
  if (masterRole?.length === 0 && !isAllowedWithoutRoles) {
    return false;
  }

  // Guest and page not for guest
  if (_.isNull(masterRole) && !isGuestOnly) {
    return false;
  }

  // Logged in and page only for guests
  if (masterRole?.length && isGuestOnly) {
    return false;
  }

  // Check - Page is only for external companies
  if (isFromInternalCompany && INTERNAL_COMPANY_HIDDEN_PAGES.includes(pageCode)) {
    return false;
  }

  // Logged in and check permissions should be skipped
  if (masterRole?.length && skipPermissionCheck) {
    return true;
  }

  // Logged in and page protected
  if (masterRole?.length && pageCode) {
    const { isViewAllowed } = getPagePermissions(pageCode)(permissions);

    return isViewAllowed;
  }

  return true;
};
